import { useState } from "react";
import { PROJECTS_ARRAY } from "../data/ResumeData";
import { Project } from "../types/Types";
import SectionHeader from "./SectionHeader";

function SkillTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md px-2 py-0.5 text-[11.5px] leading-snug bg-accent-cool/20 border border-accent-cool/60 text-fg hover:bg-accent-cool/35 hover:border-accent-cool transition-colors">
      {children}
    </span>
  );
}

function ProjectEntry({
  project,
  isOpen,
  onToggle,
}: {
  project: Project;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <article className="border-b border-rule last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full text-left py-3 group"
      >
        <div className="flex items-baseline justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-[1.0625rem] leading-tight">
              {project.projectName}
            </h3>
            <p className="mt-0.5 text-[13px] text-fg-muted line-clamp-1">
              {project.projectDesc}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[12.5px] tabular-nums whitespace-nowrap text-fg-subtle">
              {project.years}
            </span>
            <span
              aria-hidden="true"
              className={`text-[15px] text-fg-subtle group-hover:text-fg transition-all duration-300 ${isOpen ? "rotate-90" : ""}`}
            >
              ›
            </span>
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="pb-4 -mt-1 space-y-3">
          {project.cardBackgroundImageLink && (
            <a
              href={project.link ?? "#"}
              target={project.link ? "_blank" : undefined}
              rel="noreferrer"
              className="group/img block"
              {...(!project.link && { onClick: (e: React.MouseEvent) => e.preventDefault() })}
            >
              <div className="aspect-[16/9] overflow-hidden rounded-xl border border-rule bg-bg-recessed">
                <img
                  src={project.cardBackgroundImageLink}
                  alt={`${project.projectName} preview`}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover/img:scale-[1.015]"
                  loading="lazy"
                />
              </div>
            </a>
          )}

          <p className="text-[14px] leading-[1.55] text-fg-muted">
            {project.projectDesc}
          </p>

          <ul className="space-y-1.5 text-[13.5px] leading-[1.5] text-fg">
            {project.resumeBullets.map((b, i) => (
              <li key={i} className="grid grid-cols-[1.25rem_1fr] gap-1">
                <span aria-hidden="true" className="text-fg-subtle">—</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-1.5">
            {project.skills.map((s) => (
              <SkillTag key={s}>{s}</SkillTag>
            ))}
          </div>

          {project.link && (
            <a
              href={project.link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-fg text-bg px-3.5 py-1.5 text-[12.5px] font-medium hover:bg-accent hover:text-accent-fg transition-colors"
            >
              Visit project
              <span aria-hidden="true">↗</span>
            </a>
          )}
        </div>
      )}
    </article>
  );
}

function Projects() {
  const [openSet, setOpenSet] = useState<Set<number>>(new Set([0]));

  const toggle = (idx: number) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <section aria-labelledby="projects-heading">
      <SectionHeader title="Projects" />
      <h2 id="projects-heading" className="sr-only">Projects</h2>
      <div>
        {PROJECTS_ARRAY.map((p, i) => (
          <ProjectEntry
            key={p.projectName}
            project={p}
            isOpen={openSet.has(i)}
            onToggle={() => toggle(i)}
          />
        ))}
      </div>
    </section>
  );
}

export default Projects;
